import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, Routes } from '@angular/router';
import {
    type ApiTypes,
    buildFuelString,
    buildSeoCategoryFilter,
    buildSeoConditionFilter,
    buildSeoFuelFilter,
    buildTitleByFilter,
    CLOUDFLARE_IMAGE_CONFIG,
    createMetaResolver,
    createTitleResolverVehicleMarket,
    DealerFeature,
    DistributionFeature,
    FootnotesFeature,
    ImprintPageFeature,
    isHWS,
    ngrxLoaderGuard,
    PageContentType,
    SearchAboFeature,
    VehicleFeature,
} from '@hyundai/ng-common-lib';
import { provideEffects } from '@ngrx/effects';
import { getRouterSelectors } from '@ngrx/router-store';
import type { Store } from '@ngrx/store';
import { provideState } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { distributionPageTitleResolver, vehicleMarketTitleResolver } from '../../utils/title-resolver';

const { selectQueryParam } = getRouterSelectors();

const vehicleDetailRedirectOnFailUrl = (_route: ActivatedRouteSnapshot, store: Store) => {
    const dealer = store.selectSignal(DealerFeature.state.selectDealer)();
    if (dealer && dealer.slug) {
        return `/${dealer.slug}/404`;
    }
    return '/404';
};
export const vehicleProvider = [provideState(VehicleFeature.vehicleState), provideEffects(VehicleFeature.effects)];
export const distributionProvider = [
    provideState(DistributionFeature.distributionState),
    provideEffects(DistributionFeature.effects),
];
export const footnotesProvider = [provideState(ImprintPageFeature.state), provideEffects(ImprintPageFeature.effects)];
export const imprintProvider = [provideState(FootnotesFeature.state), provideEffects(FootnotesFeature.effects)];

export default [
    {
        path: '',
        loadComponent: () => import('./list-page.component'),
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: VehicleFeature.vehicleState.selectIsLoadingList,
                dataToCheckSelector: VehicleFeature.vehicleState.selectList,
                actionToDispatch: (route) => {
                    const filterQueryParam = route.queryParams['filter'] ?? undefined;

                    if (filterQueryParam !== undefined) {
                        try {
                            return VehicleFeature.vehicleActions.init({ initalFilter: JSON.parse(filterQueryParam) });
                        } catch {
                            return VehicleFeature.vehicleActions.init({});
                        }
                    }

                    return VehicleFeature.vehicleActions.init({});
                },
            }),
            ngrxLoaderGuard({
                waitUntilLoadedSelector: VehicleFeature.vehicleState.selectIsLoadingNumberOfVehicles,
                dataToCheckSelector: VehicleFeature.vehicleState.selectNumberOfVehicles,
            }),
        ],
        title: createTitleResolverVehicleMarket(async (store, isHWS) => {
            return buildTitleByFilter(store, isHWS);
        }),
        resolve: {
            meta: createMetaResolver(async (store, isHWS) => {
                const filterQueryParam = await firstValueFrom(store.select(selectQueryParam('filter')));
                if (isHWS) {
                    const dealer = await firstValueFrom(
                        store.select(DealerFeature.state.selectLoadedDealer).pipe(filter((d) => Boolean(d))),
                    );
                    return {
                        description: getDescription(filterQueryParam, dealer),
                        ogTitle: getHwsOgTitleByFilter(filterQueryParam, dealer),
                        ogDescription: getHwsOgDescription(filterQueryParam, dealer),
                        image: 'https://hyundai-images.de/cdn-cgi/image/format=auto,width=686,height=385,fit=cover/https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cmdwqthdinihw07w3r9l23p8y',
                        imageAlt:
                            'Drei moderne SUVs stehen vor einem großen, futuristisch gestalteten Bahnhofsgebäude auf einem offenen Platz. Die Fahrzeuge sind in Rot, Silber und Bronze dargestellt und nebeneinander positioniert.',
                    };
                }

                return {
                    description: await getHCMDescription(filterQueryParam, isHWS, store),
                    ogTitle: 'Fahrzeugbestand | Alle Angebote der Hyundai Promise Partner',
                    ogDescription:
                        'Finden Sie jetzt Ihr Wunschfahrzeug als Neuwagen oder geprüften Gebrauchtwagen bei Ihrem Hyundai Partner in Ihrer Nähe ► Jetzt Probefahrt anfragen!',
                    image: 'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/fetch/w_1200,h_630,c_fill,q_80,f_jpg/https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cmgyxx5anfril07uw6828wmhq',
                    imageAlt:
                        'Frontansicht eines Hyundai Fahrzeugs mit Hyundai-Promise-Kennzeichen für geprüfte Gebrauchtwagen.',
                };
            }),
        },
        pathMatch: 'full',
        providers: [
            ...vehicleProvider,
            provideState(SearchAboFeature.searchAboState),
            provideEffects(SearchAboFeature.effects),
        ],
        runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
        data: {
            contentType: PageContentType.VEHICLELIST,
        },
    },

    {
        path: ':slug',
        loadComponent: () => import('./detail-page.component'),
        providers: [vehicleProvider, imprintProvider, footnotesProvider],
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: VehicleFeature.vehicleState.selectIsLoadingDetail,
                dataToCheckSelector: VehicleFeature.vehicleState.selectDetail,
                actionToDispatch: (route) =>
                    VehicleFeature.vehicleActions.loadVehicleDetail({ slug: route.params['slug'] }),
                redirectOnFailUrl: vehicleDetailRedirectOnFailUrl,
            }),
        ],
        title: () => {
            const hws = isHWS();
            return vehicleMarketTitleResolver(
                (vehicle) =>
                    `${hws ? '' : 'Hyundai '}${vehicle.model} | ${vehicle.dealer.name} ${vehicle.dealer.location.city}`,
            )();
        },
        resolve: {
            meta: createMetaResolver(async (store, isHWS) => {
                const cloudflareImageConfig = inject(CLOUDFLARE_IMAGE_CONFIG);

                const vehicle = await firstValueFrom(store.select(VehicleFeature.vehicleState.selectLoadedDetail));
                const ogImageVehicle = `${cloudflareImageConfig}/cdn-cgi/image/format=auto,slow-connection-quality=50/${vehicle.images[0]}`;
                const ogImageFallback =
                    'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/fetch/w_1200,h_630,c_fill,q_80,f_jpg/https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cmgyxx5anfril07uw6828wmhq';
                const ogImage = vehicle.images[0] ? ogImageVehicle : ogImageFallback;
                if (isHWS) {
                    const dealer = await firstValueFrom(
                        store.select(DealerFeature.state.selectLoadedDealer).pipe(filter((d) => Boolean(d))),
                    );
                    return {
                        description: `Erleben Sie den ${vehicle.model} bei Ihrem Hyundai Händler ${dealer.name} in ${dealer.city} live. Jetzt Probefahrt anfragen!`,
                        ogTitle: `Hyundai ${vehicle.model} | ${vehicle.dealer.name} in ${vehicle.dealer.location.city}.`,
                        ogDescription: `Entdecken Sie den Hyundai ${vehicle.model} bei Ihrem Hyundai Partner ${vehicle.dealer.name} in ${vehicle.dealer.location.city} live ► Jetzt Probefahrt anfragen.`,
                        image: ogImage,
                        imageAlt: 'Abbildung eines Hyundai Fahrzeugs.',
                        type: 'product',
                    };
                }

                return {
                    description: `Erleben Sie den ${vehicle.model} bei Ihrem Hyundai Händler ${vehicle.dealer.name} in ${vehicle.dealer.location.city} live. Jetzt Probefahrt anfragen!`,
                    ogTitle: `Hyundai ${vehicle.model} | ${vehicle.dealer.name} in ${vehicle.dealer.location.city}.`,
                    ogDescription: `Entdecken Sie den Hyundai ${vehicle.model} bei Ihrem Hyundai Promise Händler ${vehicle.dealer.name} in ${vehicle.dealer.location.city} live ► Jetzt Probefahrt anfragen!`,
                    image: ogImage,
                    imageAlt: 'Abbildung eines Hyundai Fahrzeugs.',
                    type: 'product',
                };
            }),
        },
        pathMatch: 'full',
        runGuardsAndResolvers: 'pathParamsChange',
        data: {
            contentType: PageContentType.VEHICLEDETAIL,
        },
    },
    {
        path: ':slug/haendleranfrage/haendlerkontakt',
        loadComponent: () => import('../checkouts/general-request/general-request.component'),
        providers: [vehicleProvider, distributionProvider],
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: VehicleFeature.vehicleState.selectIsLoadingDetail,
                dataToCheckSelector: VehicleFeature.vehicleState.selectDetail,
                actionToDispatch: (route) =>
                    VehicleFeature.vehicleActions.loadVehicleDetail({ slug: route.params['slug'] }),
                redirectOnFailUrl: vehicleDetailRedirectOnFailUrl,
            }),
        ],
        title: (route: ActivatedRouteSnapshot) => distributionPageTitleResolver(route)(),
        resolve: {
            meta: createMetaResolver(async (store) => {
                const cloudflareImageConfig = inject(CLOUDFLARE_IMAGE_CONFIG);
                const vehicle = await firstValueFrom(store.select(VehicleFeature.vehicleState.selectLoadedDetail));
                const ogImageVehicle = `${cloudflareImageConfig}/cdn-cgi/image/format=auto,slow-connection-quality=50/${vehicle.images[0]}`;
                const ogImageFallback =
                    'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/fetch/w_1200,h_630,c_fill,q_80,f_jpg/https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cmgyxx5anfril07uw6828wmhq';
                const ogImage = vehicle.images[0] ? ogImageVehicle : ogImageFallback;

                return {
                    description: `Kontakt aufnehmen mit ${vehicle.dealer.name} in ${vehicle.dealer.location.city} zum ${vehicle.model}. Rückruf, Angebot oder allgemeine Anfrage – wir sind für Sie da.`,
                    ogTitle: `Händlerkontakt – ${vehicle.model} bei ${vehicle.dealer.name}`,
                    ogDescription: `Jetzt Kontakt aufnehmen mit ${vehicle.dealer.name} in ${vehicle.dealer.location.city}. Rückrufwunsch, Angebotsanfrage oder allgemeine Frage zum ${vehicle.model}.`,
                    image: ogImage,
                    imageAlt: 'Abbildung eines Hyundai Fahrzeugs.',
                };
            }),
        },
        pathMatch: 'full',
        data: {
            contentType: PageContentType.CARMARKETFORM,
        },
    },
    {
        path: ':slug/haendleranfrage/probefahrt',
        loadComponent: () => import('../checkouts/test-drive/test-drive.component'),
        providers: [vehicleProvider, distributionProvider],
        canActivate: [
            ngrxLoaderGuard({
                waitUntilLoadedSelector: VehicleFeature.vehicleState.selectIsLoadingDetail,
                dataToCheckSelector: VehicleFeature.vehicleState.selectDetail,
                actionToDispatch: (route) =>
                    VehicleFeature.vehicleActions.loadVehicleDetail({ slug: route.params['slug'] }),
                redirectOnFailUrl: vehicleDetailRedirectOnFailUrl,
            }),
        ],
        title: (route: ActivatedRouteSnapshot) => distributionPageTitleResolver(route)(),
        resolve: {
            meta: createMetaResolver(async (store) => {
                const cloudflareImageConfig = inject(CLOUDFLARE_IMAGE_CONFIG);
                const vehicle = await firstValueFrom(store.select(VehicleFeature.vehicleState.selectLoadedDetail));
                const ogImageVehicle = `${cloudflareImageConfig}/cdn-cgi/image/format=auto,slow-connection-quality=50/${vehicle.images[0]}`;
                const ogImageFallback =
                    'https://res.cloudinary.com/hmd-pw-dfnosvs4u/image/fetch/w_1200,h_630,c_fill,q_80,f_jpg/https://eu-central-1-shared-euc1-02.graphassets.com/cluk4akwq0hkd07w15cu50y0w/cmgyxx5anfril07uw6828wmhq';
                const ogImage = vehicle.images[0] ? ogImageVehicle : ogImageFallback;

                return {
                    description: `Probefahrt anfragen für den ${vehicle.model} bei ${vehicle.dealer.name} in ${vehicle.dealer.location.city}. Jetzt Wunschtermin auswählen und direkt beim Händler anfragen.`,
                    ogTitle: `Probefahrt – ${vehicle.model} bei ${vehicle.dealer.name}`,
                    ogDescription: `Jetzt Probefahrt für den ${vehicle.model} bei ${vehicle.dealer.name} in ${vehicle.dealer.location.city} anfragen. Wunschtermin und Tageszeit wählbar.`,
                    image: ogImage,
                    imageAlt: 'Abbildung eines Hyundai Fahrzeugs.',
                };
            }),
        },
        pathMatch: 'full',
        data: {
            contentType: PageContentType.CARMARKETFORM,
        },
    },
] satisfies Routes;

function getDescription(filterQueryParam: string | string[] | undefined, dealer?: ApiTypes.Dealer | null) {
    if (dealer) {
        const postDealerText = `bei Ihrem Hyundai Partner ${dealer.name} in ${dealer.city}!`;
        if (!filterQueryParam) {
            return `Finden Sie jetzt Ihr Wunschmodell ${postDealerText}`;
        }
        if (!Array.isArray(filterQueryParam)) {
            const filter = JSON.parse(filterQueryParam);
            return getDescriptionByFilter(filter, postDealerText);
        }
    }
    return 'Finden Sie jetzt Ihr Hyundai Wunschmodell aus dem Hyundai Gebrauchtwagenprogramm bei Ihrem Hyundai Partner.';
}

function getHwsOgDescription(filterQueryParam: string | string[] | undefined, dealer: ApiTypes.Dealer | null) {
    if (dealer) {
        const postDealerText = `${dealer.name} in ${dealer.city}!`;
        if (!filterQueryParam) {
            return `Entdecken Sie das Hyundai Fahrzeugangebot von ${postDealerText}. ► Jetzt Wunschmodell finden und Probefahrt vereinbaren.`;
        }
        if (!Array.isArray(filterQueryParam)) {
            const filter = JSON.parse(filterQueryParam);
            return getHwsOgDescriptionByFilter(filter, postDealerText);
        }
    }
    return 'Finden Sie jetzt Ihr Hyundai Wunschmodell aus dem Hyundai Gebrauchtwagenprogramm bei Ihrem Hyundai Partner.';
}

function getHwsOgDescriptionByFilter(filter: { fuel?: string; condition?: string }, postDealerText: string) {
    const { fuel, condition } = filter;
    if (fuel?.includes('Elektro')) {
        return `Alle Hyundai Elektrofahrzeuge von ${postDealerText} im Überblick. ► Jetzt Wunschmodell finden und Probefahrt vereinbaren.`;
    }
    if (condition?.includes('Gebrauchtfahrzeug')) {
        return `Attraktive Hyundai Gebrauchtwagen von ${postDealerText}. ► Jetzt Wunschmodell finden und Probefahrt vereinbaren.`;
    }
    if (condition?.includes('Neufahrzeug')) {
        return `Attraktive Hyundai Neuwagen von ${postDealerText}. ► Jetzt Wunschmodell finden und Probefahrt vereinbaren.`;
    }
    return `Entdecken Sie das Hyundai Fahrzeugangebot von ${postDealerText}. ► Jetzt Wunschmodell finden und Probefahrt vereinbaren.`;
}

function getHwsOgTitleByFilter(filterQueryParam: string | string[] | undefined, dealer: ApiTypes.Dealer) {
    const postDealerText = `${dealer.name} in ${dealer.city}!`;
    if (!filterQueryParam) {
        return `Hyundai Fahrzeugbestand | ${postDealerText}`;
    }
    if (!Array.isArray(filterQueryParam)) {
        const filter = JSON.parse(filterQueryParam);
        const { fuel, condition } = filter;
        if (fuel?.includes('Elektro')) {
            return `Hyundai Elektrofahrzeuge | ${postDealerText}`;
        }
        if (condition?.includes('Gebrauchtfahrzeug')) {
            return `Hyundai Gebrauchtwagen | ${postDealerText}`;
        }
        if (condition?.includes('Neufahrzeug')) {
            return `Hyundai Neuwagen | ${postDealerText}`;
        }
    }
    return `Hyundai Fahrzeugbestand | ${postDealerText}`;
}

function getDescriptionByFilter(filter: { fuel?: string; condition?: string }, postDealerText: string) {
    const { fuel, condition } = filter;
    if (fuel?.includes('Elektro')) {
        return `Entdecken Sie Elektro Neuwagen und Gebrauchtwagen ${postDealerText}`;
    }
    if (condition?.includes('Gebrauchtfahrzeug')) {
        return `Finden Sie jetzt Ihr Wunschmodell aus dem Hyundai Gebrauchtwagenbestand ${postDealerText}`;
    }
    if (condition?.includes('Neufahrzeug')) {
        return `Finden Sie jetzt Ihr Wunschmodell als Neuwagen ${postDealerText}`;
    }
    return `Finden Sie jetzt Ihr Wunschmodell ${postDealerText}`;
}

async function getHCMDescription(filterQueryParam: string | string[] | undefined, isHWS: boolean, store: Store) {
    let description = 'Entdecken Sie ';
    const numberOfVehicles = await firstValueFrom(
        store.select(VehicleFeature.vehicleState.selectLoadedNumberOfVehicles),
    );
    if (numberOfVehicles && numberOfVehicles > 0 && filterQueryParam && typeof filterQueryParam === 'string') {
        const { condition, model, fuel, category, hybridPlugin } = JSON.parse(filterQueryParam);
        const modelFilter = Array.isArray(model) && model.length === 1 ? model[0] : undefined;
        const fuelFilter = buildFuelString(hybridPlugin, fuel);
        const categoryFilter =
            Array.isArray(category) && category.length === 1 ? buildSeoCategoryFilter(category[0]) : undefined;
        const conditionFilter = buildSeoConditionFilter(condition);
        const newOrUsed = ' ✓ neu oder gebraucht';
        if (modelFilter && conditionFilter) {
            description += `den Hyundai ${modelFilter} als ${conditionFilter}`;
        } else if (modelFilter && fuelFilter) {
            const fuelFilterString = buildSeoFuelFilter(fuelFilter);
            const showFuelFilter = !(
                fuelFilterString && modelFilter.toLowerCase().includes(fuelFilterString.toLowerCase())
            );
            description += `${showFuelFilter ? '' : 'den '}Hyundai ${modelFilter} ${showFuelFilter ? `${buildSeoFuelFilter(fuelFilter)} Modelle` : ''}`;
        } else if (modelFilter && categoryFilter) {
            description += `Hyundai ${modelFilter} ${categoryFilter} Modelle`;
        } else if (modelFilter) {
            description += `den Hyundai ${modelFilter}`;
        } else if (categoryFilter && fuelFilter) {
            description += `Hyundai ${buildSeoFuelFilter(fuelFilter)} ${categoryFilter} Modelle`;
        } else if (categoryFilter && conditionFilter) {
            description += `Hyundai ${categoryFilter}  Modelle als ${conditionFilter}`;
        } else if (categoryFilter) {
            description += `Hyundai ${categoryFilter} Modelle`;
        } else if (fuelFilter && conditionFilter) {
            description += `Hyundai ${buildSeoFuelFilter(fuelFilter)} Modelle als ${conditionFilter}`;
        } else if (fuelFilter) {
            description += `Hyundai ${buildSeoFuelFilter(fuelFilter)} Modelle`;
        } else if (conditionFilter) {
            description += `Hyundai ${conditionFilter}`;
        } else {
            description += 'Hyundai Modelle';
        }

        let dealerString = '';
        if (isHWS) {
            const dealer = await firstValueFrom(
                store.select(DealerFeature.state.selectLoadedDealer).pipe(filter((d) => Boolean(d))),
            );
            dealerString = ` ${dealer.name} in ${dealer.city}`;
        }
        description += `${conditionFilter ? '' : newOrUsed} ✓ mit Garantie & Rundum-Service bei Ihrem Hyundai Partner${dealerString}. ► Jetzt Probefahrt buchen!`;
    } else {
        description =
            'Finden Sie jetzt Ihr Wunschmodell aus dem Angebot der sofort verfügbaren Hyundai Neuwagen und Gebrauchtwagen bei Ihrem Hyundai Promise Partner.';
    }
    return description;
}
